import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { isPortrait } from "@multimediallc/web-utils"
import { mergeClasses } from "../../../../../../../utils/css"
import "../../filterSection.scss"

const MAX_ROWS = 8
const MAX_ROWS_COMBINED_PANEL = 4
const MAX_ROWS_LANDSCAPE = 3

interface PageInfo {
    startIndex: number
    endIndex: number
}

interface UseTagPaginationProps {
    tags: string[]
    combinedOverlay: boolean
}

function getMaxRows(combinedOverlay: boolean): number {
    if (combinedOverlay) {
        return MAX_ROWS_COMBINED_PANEL
    }

    if (!isPortrait()) {
        return MAX_ROWS_LANDSCAPE
    }
    return MAX_ROWS
}

function getMeasurementDivStyle(): React.CSSProperties {
    // window.innerHeight and innerWidth do not update before this runs, so we have to
    // choose between then depending on isPortait, which does update in time
    const smallSidePx = Math.min(window.innerWidth, window.innerHeight)
    const longSidePx = Math.max(window.innerWidth, window.innerHeight)
    return {
        position: "fixed",
        visibility: "hidden",
        zIndex: 2000,
        margin: "10px 16px 24px",
        top: 0,
        left: 0,
        width: `${(isPortrait() ? smallSidePx : longSidePx) - 32}px`, // 16 margin left and right
    }
}

export function useTagPagination({
    tags,
    combinedOverlay,
}: UseTagPaginationProps) {
    const [currentPage, setCurrentPage] = useState(1)
    const [pageData, setPageData] = useState<PageInfo[]>([])
    const measurementRef = useRef<HTMLDivElement>(null)
    const tagRefs = useRef<(HTMLButtonElement | null)[]>([])

    useLayoutEffect(() => {
        if (!measurementRef.current || tags.length === 0) {
            const targetPageData = [{ startIndex: 0, endIndex: tags.length }]
            if (JSON.stringify(pageData) !== JSON.stringify(targetPageData)) {
                setPageData([{ startIndex: 0, endIndex: tags.length }])
            }
            if (currentPage !== 1) {
                setCurrentPage(1)
            }
            return
        }

        const measuredPages: PageInfo[] = []
        let currentStartIndex = 0
        let rowCount = 0
        let lastOffsetTop = -1
        let pageEndIndex = 0

        for (let i = 0; i < tags.length; i++) {
            const tagElement = tagRefs.current[i]
            if (!tagElement) continue

            const offsetTop = tagElement.offsetTop

            // Check if this tag started a new row
            if (offsetTop > lastOffsetTop) {
                rowCount++
                lastOffsetTop = offsetTop
            }

            if (rowCount > getMaxRows(combinedOverlay)) {
                pageEndIndex = i // first on the *next* page
                measuredPages.push({
                    startIndex: currentStartIndex,
                    endIndex: pageEndIndex,
                })
                currentStartIndex = pageEndIndex
                rowCount = 1
                lastOffsetTop = tagElement.offsetTop
            }
        }

        // Add the last page
        if (currentStartIndex < tags.length) {
            measuredPages.push({
                startIndex: currentStartIndex,
                endIndex: tags.length,
            })
        }
        const finalPages =
            measuredPages.length > 0
                ? measuredPages
                : [{ startIndex: 0, endIndex: tags.length }]
        const targetPage = Math.min(currentPage, finalPages.length || 1)

        const pageDataChanged =
            JSON.stringify(finalPages) !== JSON.stringify(pageData)

        if (pageDataChanged) {
            setPageData(finalPages)
        }

        if (currentPage !== targetPage) {
            setCurrentPage(targetPage)
        }
    }, [tags, screen.orientation?.type])

    const totalPages = pageData.length

    const currentTags = useMemo(() => {
        if (pageData.length === 0 || !pageData[currentPage - 1]) {
            // fallback if measurement hasn't run or failed
            const fallbackItemsPerPage = 24
            const fallbackStartIndex = (currentPage - 1) * fallbackItemsPerPage
            return tags.slice(
                fallbackStartIndex,
                fallbackStartIndex + fallbackItemsPerPage,
            )
        }
        const { startIndex, endIndex } = pageData[currentPage - 1]
        return tags.slice(startIndex, endIndex)
    }, [tags, currentPage, pageData])

    const nextPage = useCallback(() => {
        setCurrentPage((prev) => (prev === totalPages ? 1 : prev + 1))
    }, [totalPages])

    const prevPage = useCallback(() => {
        setCurrentPage((prev) => (prev === 1 ? totalPages : prev - 1))
    }, [totalPages])

    const toFirstPage = useCallback(() => {
        setCurrentPage(1)
    }, [])

    return {
        currentPage,
        totalPages,
        currentTags,
        nextPage,
        prevPage,
        toFirstPage,
        renderMeasurementDiv: (
            renderTag: (
                tag: string,
                registerRef: (el: HTMLButtonElement | null) => void,
            ) => React.ReactNode,
        ) => (
            <div
                className={mergeClasses(
                    combinedOverlay
                        ? "FilterSection__filterOptionsCombined"
                        : "FilterSection__filterOptions",
                    "Hashtags",
                )}
                ref={measurementRef}
                aria-hidden="true"
                style={getMeasurementDivStyle()}
            >
                {tags.map((tag, index) =>
                    renderTag(tag, (el) => {
                        tagRefs.current[index] = el
                    }),
                )}
            </div>
        ),
    }
}
